/**
 * Media host table: everything the media proxy knows about each site it fetches from, in one place.
 *
 * Before this, the rules lived as `url.includes(...)` checks spread over the proxy route (allowlist, headers, address
 * guessing, retries) and lib/image-utils.ts (which hosts the browser loads directly). Now:
 * - the proxy allowlist is every `domains` entry below (a domain also covers its subdomains);
 * - `headers` are what that site's media server wants to see (usually its own Referer);
 * - `legacyAlternates` are the addresses the old proxy GUESSED when the real one failed (other extensions, sample vs
 *   full). They are kept so nothing regresses, but they are a stopgap: the goal is for each source to give its exact
 *   addresses so these can be deleted host by host. The proxy caps how many it tries.
 * - `direct` hosts are loaded straight by the browser, not through the proxy.
 *
 * Plain data and pure functions only: imported by the server route and by the browser (lib/image-utils.ts).
 */

export interface MediaHost {
  /** Short name for logs and the audit. */
  name: string
  /** Host names this entry covers; each also covers its subdomains. */
  domains: string[]
  /** An address that makes the visitor's OWN browser save this file straight from the host, with no bytes through our server: a plain
   * navigation to a link the host serves as a download (application/octet-stream). Tried by the viewer after a CORS read fails and before the
   * streaming proxy. null = this file has no such link. */
  directDownload?: (url: string) => string | null
  /** Extra request headers for this site's media server. */
  headers?: (url: string, ctx: { range?: string }) => Record<string, string>
  /** Addresses to try when the given one fails (old guessing; to be removed once the source gives exact addresses). */
  legacyAlternates?: (url: string) => string[]
  /** Retry the same address after these waits when it answers one of these statuses. */
  retry?: { statuses: number[]; delaysMs: number[] }
  /** Last-resort relays (full URLs) when every direct attempt failed. */
  relays?: (url: string) => { name: string; url: string }[]
  /** Whether the browser should load this address directly instead of through the proxy. */
  direct?: (url: string) => boolean
  /** Its pictures go through the Cloudflare media Worker (lib/media-worker.ts) instead of the Vercel proxy, when the Worker is set up.
   * For hosts whose pictures need our headers (so the browser cannot load them itself) and are big enough that Vercel's per-GB
   * billing matters. Every other host's pictures are refused by the Worker. */
  workerPictures?: boolean
  /** Saving its files NEVER falls back to the Vercel proxy (owner decision 2026-10-28: Hypnohub's files are huge, up to tens of MB). The viewer
   * saves them through the media Worker only; if that is not set up or refuses, the file opens in a new tab instead. */
  downloadWorkerOnly?: boolean
}

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp"]
const VIDEO_URL = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|#|$)/i

const extOf = (url: string) => url.split("?")[0].split(".").pop()?.toLowerCase() || ""
const withoutExt = (url: string) => {
  const q = url.indexOf("?")
  const path = q >= 0 ? url.slice(0, q) : url
  const dot = path.lastIndexOf(".")
  return dot > path.lastIndexOf("/") ? path.slice(0, dot) : path
}
const swapExts = (url: string, exts: string[]) => {
  const current = extOf(url)
  const base = withoutExt(url)
  return exts.filter((ext) => ext !== current).map((ext) => `${base}.${ext}`)
}

/** Browser-like headers for a picture request to a cross-site image server. */
const imageFetchHeaders = (referer?: string, extra: Record<string, string> = {}): Record<string, string> => ({
  Accept: "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
  "Accept-Language": "en-US,en;q=0.9",
  "Cache-Control": "no-cache",
  "Sec-Fetch-Dest": "image",
  "Sec-Fetch-Mode": "no-cors",
  "Sec-Fetch-Site": "cross-site",
  ...(referer ? { Referer: referer } : {}),
  ...extra,
})

const CHROME_UA = "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36"

export const MEDIA_HOSTS: MediaHost[] = [
  {
    name: "danbooru",
    domains: ["danbooru.donmai.us", "cdn.donmai.us", "donmai.us"],
    headers: () => ({
      "User-Agent": "Goonru/1.0 (Image Proxy; +https://github.com/goonru)",
      Accept: "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
      "Accept-Language": "en-US,en;q=0.9",
      Referer: "https://danbooru.donmai.us/",
      "Cache-Control": "no-cache",
    }),
    // No address guessing: checked 2026-10-16 against 90 live Danbooru addresses (pictures and animated), none failed as given.
  },
  { name: "safebooru", domains: ["safebooru.org"] },
  // Foot Fetish Booru and the other booru.org boards: pictures (img.) and thumbnails (thumbs.) answer a browser with any referer (checked 2026-10-28
  // with localhost as the referer), so the visitor loads them directly and nothing goes through our server. They send no CORS header, so saving
  // falls back to the download proxy, which needs this entry.
  { name: "footfetishbooru", domains: ["booru.org"], direct: () => true, headers: () => imageFetchHeaders("https://footfetishbooru.booru.org/") },
  {
    name: "rule34",
    domains: ["rule34.xxx"],
    headers: () => imageFetchHeaders("https://rule34.xxx/"),
    // No address guessing: checked 2026-10-16 against about 650 live Rule34.xxx addresses (pictures, samples, videos), none failed as given.
  },
  {
    name: "gelbooru",
    domains: ["gelbooru.com"],
    headers: () => imageFetchHeaders(),
    // Gelbooru videos stream better straight from its CDN; its pictures need the proxy's referer (hotlinks get HTML).
    direct: (url) => VIDEO_URL.test(url),
  },
  {
    name: "sankaku",
    domains: ["sankakucomplex.com"],
    // The file host answers a Referer of its own address (what the download route adds by default) with a redirect that ends in a 503; the main site or none works.
    headers: () => imageFetchHeaders("https://www.sankakucomplex.com/", { "User-Agent": "SCChannelApp/3.0 (Android; sankaku)" }),
  },
  {
    name: "rule34hentai",
    domains: ["rule34hentai.net"],
    headers: () => imageFetchHeaders(),
    direct: () => true,
  },
  {
    name: "rule34xyz",
    domains: ["rule34.xyz", "rule34xyz.b-cdn.net"],
    headers: () => imageFetchHeaders(),
    // No address guessing: the source builds exact addresses (the videos' copies and the images' medium size come from the post's `files`);
    // checked 2026-10-16 on about 50 live addresses, none failed as given.
  },
  {
    name: "rule34us",
    domains: ["rule34.us"],
    // KEPT ON PURPOSE (2026-10-16): the listing page shows only thumbnails, and the real file's extension (.jpeg for most, .png for some;
    // the listing code assumes .jpg, wrong for 20 of 21 checked) is on the post page. Reading it for every post would cost one extra
    // request each, more than the one or two failed tries this guess costs.
    headers: () => ({
      Accept: "image/avif,image/webp,image/png,image/svg+xml,image/*;q=0.8,*/*;q=0.5",
      "Accept-Language": "en-US,en;q=0.9",
      Referer: "https://rule34.us/",
      "Sec-Fetch-Dest": "image",
      "Sec-Fetch-Mode": "no-cors",
      "Sec-Fetch-Site": "cross-site",
    }),
    legacyAlternates: (url) => {
      if (!url.includes("/images/")) return []
      const out = swapExts(url, IMAGE_EXTS)
      if (url.includes("sample_")) {
        const full = url.replace("sample_", "")
        out.push(full, ...swapExts(full, IMAGE_EXTS))
      }
      return out
    },
  },
  {
    name: "tbib",
    domains: ["tbib.org"],
    headers: () => imageFetchHeaders(),
    // No address guessing: the source now builds the sample address exactly (always .jpg).
    retry: { statuses: [429], delaysMs: [2000] },
  },
  {
    name: "realbooru",
    domains: ["realbooru.com"],
    // KEPT ON PURPOSE (2026-10-16): same reason as Rule34.us. The listing code assumes .jpg; the real files are .jpeg for most (13 of
    // 14 wrong ones checked) or .gif where it assumed .webm, and only the post page knows.
    legacyAlternates: (url) => swapExts(url, ["webm", "mp4", ...IMAGE_EXTS]),
  },
  {
    name: "hitomi",
    domains: ["hitomi.la", "gold-usergeneratedcontent.net"],
    headers: () => ({
      Accept: "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
      "Accept-Language": "en-US,en;q=0.9",
      Referer: "https://hitomi.la/",
      Origin: "https://hitomi.la",
      "Sec-Fetch-Dest": "image",
      "Sec-Fetch-Mode": "cors",
      "Sec-Fetch-Site": "cross-site",
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    }),
    retry: { statuses: [503], delaysMs: [1500, 3000, 5000] },
    // Hitomi answers 404 without its Referer, so the browser cannot load its pictures itself, and an album is dozens of full-size
    // pages: they go through the Worker, which Cloudflare does not bill per GB (2026-10-23).
    workerPictures: true,
    // Only our own relay, and only when HITOMI_PROXY_URL is set. The free public relays (corsproxy.org, proxy6.net, allorigins.win)
    // were removed 2026-10-16: they saw every Hitomi address we fetched, none of them fired in 7 days of live logs, and if Hitomi's
    // host is ever blocked from our server the fix is our own Cloudflare Worker (as for Gelbooru).
    relays: (url) => {
      const custom = typeof process !== "undefined" ? process.env.HITOMI_PROXY_URL : undefined
      return custom ? [{ name: "custom (HITOMI_PROXY_URL)", url: custom.replace("{url}", encodeURIComponent(url)) }] : []
    },
  },
  {
    name: "doujins",
    domains: ["doujins.com"],
    // Plain pictures (static.doujins.com/f-..., gallery pages) load from any referrer, so the visitor's browser loads them straight from the
    // site and they skip our proxy and its bandwidth. The SIGNED covers of the Recent/Featured/Movies feeds (f2-...?st=...&e=...) answer
    // 403 + HTML to any outside referrer, which Chrome blocks (ERR_BLOCKED_BY_ORB), so those stay on the proxy, which sends doujins.com
    // (checked 2026-10-23: no referrer or doujins.com 200, goonru.app or localhost 403, unless Doujins' CDN already holds a copy).
    // Downloads do not use this flag (they have their own route).
    direct: (url) => !/[?&]st=/.test(url),
    headers: () => ({
      Accept: "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
      "Accept-Language": "en-US,en;q=0.9",
      Referer: "https://doujins.com/",
      Origin: "https://doujins.com",
      "Sec-Fetch-Dest": "image",
      "Sec-Fetch-Mode": "cors",
      "Sec-Fetch-Site": "same-site",
      "Cache-Control": "no-cache",
    }),
  },
  {
    name: "xlecx",
    domains: ["xlecx.one", "xlecx.shop"],
    headers: () => ({
      Accept: "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
      "Accept-Language": "en-US,en;q=0.9",
      Referer: "https://xlecx.one/",
      Origin: "https://xlecx.one",
      "Sec-Fetch-Dest": "image",
      "Sec-Fetch-Mode": "cors",
      "Sec-Fetch-Site": "same-site",
      "Cache-Control": "no-cache",
    }),
  },
  {
    name: "erome",
    domains: ["erome.com"],
    headers: (url, { range }) => {
      // Erome's CDN wants its own referer, and refuses video requests that are not Range requests.
      const isVideo = /\.(mp4|webm|mov)(\?|#|$)/i.test(url)
      return {
        "User-Agent": CHROME_UA,
        Accept: isVideo ? "*/*" : "image/avif,image/webp,image/apng,image/*,*/*;q=0.8",
        "Accept-Language": "en-US,en;q=0.9",
        Referer: "https://www.erome.com/",
        Origin: "https://www.erome.com",
        "Sec-Fetch-Dest": isVideo ? "video" : "image",
        "Sec-Fetch-Mode": "no-cors",
        "Sec-Fetch-Site": "cross-site",
        ...(isVideo && !range ? { Range: "bytes=0-" } : {}),
      }
    },
  },
  // JAVAPI cover pictures (watchhentai.net, fourhoi.mrstcdn.store) load from any referrer (checked 2026-10-25), so the browser loads them itself and
  // they skip our proxy; the video players are separate (lib/player-embed.ts).
  { name: "javapi-hentai", domains: ["watchhentai.net", "apijav.com"], headers: () => ({ Accept: "image/avif,image/webp,image/apng,image/*,*/*;q=0.8", Referer: "https://watchhentai.net/" }), direct: (url) => /\.(jpe?g|png|webp|gif|avif)(\?|#|$)/i.test(url) },
  { name: "javapi-jav", domains: ["quantix2.top", "mrstcdn.store"], direct: (url) => !url.includes("quantix2.top") && /\.(jpe?g|png|webp|gif|avif)(\?|#|$)/i.test(url), headers: (url): Record<string, string> => (url.includes("quantix2.top") ? { Accept: "image/avif,image/webp,image/apng,image/*,*/*;q=0.8", Referer: "https://server.apijav.com/" } : {}) },
  {
    name: "civitai",
    domains: ["civitai.com", "civitai.red"],
    headers: () => imageFetchHeaders("https://civitai.com/", { "User-Agent": CHROME_UA }),
  },
  {
    name: "pixiv",
    domains: ["pximg.net"],
    headers: () => imageFetchHeaders("https://www.pixiv.net/", { Origin: "https://www.pixiv.net" }),
  },
  { name: "rule34lol", domains: ["rule34.lol"], direct: () => true },
  // Zerochan's picture hosts (s3. / static.) answer any Referer and the browser can load them itself; without an entry here the proxy refused them
  // ("Host not in allowlist") and no Zerochan picture could load in the app.
  { name: "zerochan", domains: ["zerochan.net"], direct: () => true },
  { name: "arfreaks", domains: ["arfreaks.com"], direct: (url) => url.includes("videos.arfreaks.com") },
  // Hosts with no special rules: the proxy sends the URL's own origin as Referer.
  { name: "sakugabooru", domains: ["sakugabooru.com"] },
  { name: "yandere", domains: ["yande.re"] },
  // Goontai covers (hentaiocean.com/assets/cover/....webp) load from any referrer (checked 2026-10-25), so the browser loads them itself; the video
  // players are separate (lib/player-embed.ts).
  { name: "goontai", domains: ["hentaiocean.com"], direct: (url) => /\.(webp|jpe?g|png|gif|avif)(\?|#|$)/i.test(url) },
  // HentaiEra pictures (m11.hentaiera.com ...) load from any referrer and send CORS *, checked 2026-10-23: loaded straight by the browser.
  { name: "hentaiera", domains: ["hentaiera.com"], direct: () => true },
  { name: "lewdvault", domains: ["lewdvault.com"] },
  { name: "wholesomelist", domains: ["wholesomelist.com"] },
  // e926 shuts out every datacenter address, so our proxy gets 502 on its pictures. The visitor's browser loads them straight from e926
  // (checked 2026-10-16: loads with the normal referrer, blocked with none). Must come before the shared e621 entry below.
  { name: "e926", domains: ["e926.net"], direct: () => true },
  { name: "e621", domains: ["e621.net", "e926.net", "e6ai.net"] },
  { name: "sexvr", domains: ["sexvr.com"] },
  // VRHump videos (cdn-r2.vrhump.com) are full-length MP4s of 100s of MB: loaded straight by the browser, not streamed through our
  // proxy. The host sends Access-Control-Allow-Origin: * and accepts our referer (checked 2026-10-12), so the VR player can use them.
  { name: "vrhump", domains: ["vrhump.com"], direct: (url) => url.includes("cdn-r2.vrhump.com") && VIDEO_URL.test(url) },
  { name: "vrpornbase", domains: ["vrpornbase.com"] },
  { name: "4chan", domains: ["4cdn.org"] },
  { name: "derpibooru", domains: ["derpicdn.net"] },
  { name: "hypnohub", domains: ["hypnohub.net"], downloadWorkerOnly: true },
  { name: "paheal", domains: ["paheal.net", "paheal-cdn.net"] },
  { name: "xbooru", domains: ["xbooru.com"] },
  // XVideos MP4s (mp4-cdn77.xvideos-cdn.com): 6 of 6 samples were real video, answered range requests from our site's origin and
  // sent Access-Control-Allow-Origin (checked 2026-10-13), so the browser plays them itself instead of streaming through our proxy.
  // Pictures from the same host stay proxied.
  { name: "xvideos", domains: ["xvideos-cdn.com"], direct: (url) => url.includes("mp4-cdn77.xvideos-cdn.com") && VIDEO_URL.test(url) },
  { name: "eporner", domains: ["eporner.com"] },
  // VR Porn Base's files (cloud.vrphub.com) send no CORS header and need no Referer. Its "?download" form is served as application/octet-stream,
  // so the visitor's browser saves it straight from the host (checked 2026-10-27); the file itself plays in a video element.
  { name: "vrpornbase", domains: ["vrphub.com"], directDownload: (url) => (/\.mp4(\?|#|$)/i.test(url) ? `${url.split(/[?#]/)[0]}?download` : null) },
  { name: "redgifs", domains: ["redgifs.com"] },
  { name: "scrolller", domains: ["scrolller.com"] },
  { name: "kemono", domains: ["kemono.cr"] },
  { name: "pawchive", domains: ["pawchive.pw"] },
  { name: "audiochan", domains: ["audiochan.com"] },
  { name: "app-storage", domains: ["blob.vercel-storage.com"] },
]

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase()
  } catch {
    return null
  }
}

const matches = (hostname: string, domain: string) => hostname === domain || hostname.endsWith(`.${domain}`)

/** The table entry for a URL's host, or null when the host is not one the proxy may fetch from. */
export function findMediaHost(url: string): MediaHost | null {
  const hostname = hostnameOf(url)
  if (!hostname) return null
  return MEDIA_HOSTS.find((host) => host.domains.some((domain) => matches(hostname, domain))) ?? null
}

/** Every address to try for a URL, in order: the URL itself, then its host's legacy guesses, capped. */
export function mediaCandidates(url: string, max = 8): string[] {
  const host = findMediaHost(url)
  const list = [url, ...(host?.legacyAlternates?.(url) ?? [])]
  return Array.from(new Set(list)).filter((candidate) => findMediaHost(candidate) !== null).slice(0, max)
}
